/**
 * Centrala formateringsfunktioner för hela applikationen
 *
 * Svensk nummerformatering: mellanslag som tusentalsavgränsare
 * Exempel: 1 234 567, -12 345, 0
 */

/**
 * Formatera ett tal med svenska tusentalsavgränsare (mellanslag)
 * @param value - Talet att formatera
 * @param showZero - Visa "0" eller tom sträng för nollvärden (default: true)
 * @returns Formaterat tal som sträng
 */
export function formatNumber(value: number | null | undefined, showZero = true): string {
  if (value === null || value === undefined) {
    return showZero ? "0" : "";
  }

  if (value === 0 && !showZero) {
    return "";
  }

  // Använd sv-SE locale för korrekt svensk formatering
  return new Intl.NumberFormat("sv-SE", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(value);
}

/**
 * Formatera ett tal som valuta (kr)
 * @param value - Talet att formatera
 * @param showCurrency - Visa "kr" suffix (default: true)
 */
export function formatCurrency(value: number | null | undefined, showCurrency = true): string {
  const formatted = formatNumber(value);
  return showCurrency ? `${formatted} kr` : formatted;
}

/**
 * Formatera ett tal som procent
 * @param value - Talet att formatera (0-100 skala)
 * @param decimals - Antal decimaler (default: 1)
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) {
    return "0%";
  }

  return new Intl.NumberFormat("sv-SE", {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value) + "%";
}

/**
 * Formatera ett tal kompakt (tkr / mkr / mdr) för begränsat utrymme
 * @param value - Talet att formatera
 * @param showCurrency - Visa enhet (default: true)
 * @returns Kompakt sträng, t.ex. "2,5 mkr", "523 tkr", "850 kr"
 *
 * Formatering:
 * - >= 1 miljard: "X,X mdr" (1 decimal om < 10 mdr, annars heltal)
 * - >= 1 miljon: "X,X mkr" (1 decimal om < 10 mkr, annars heltal)
 * - >= 1 000: "X tkr" (heltal)
 * - < 1 000: "X kr" (heltal)
 */
export function formatCompact(value: number | null | undefined, showCurrency = true): string {
  if (value === null || value === undefined || value === 0) {
    return showCurrency ? "0 kr" : "0";
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : ""; // Use proper minus sign

  // Miljarder (>= 1 000 000 000)
  if (abs >= 1_000_000_000) {
    const mdr = abs / 1_000_000_000;
    const decimals = mdr >= 10 ? 0 : 1;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(mdr);
    return showCurrency ? `${sign}${formatted} mdr` : `${sign}${formatted}B`;
  }

  // Miljoner (>= 1 000 000)
  if (abs >= 1_000_000) {
    const mkr = abs / 1_000_000;
    const decimals = mkr >= 10 ? 0 : 1;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(mkr);
    return showCurrency ? `${sign}${formatted} mkr` : `${sign}${formatted}M`;
  }

  // Tusen (>= 1 000)
  if (abs >= 1_000) {
    const tkr = abs / 1_000;
    // Visa decimal för 1-9 tkr, annars heltal
    const decimals = tkr < 10 ? 1 : 0;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(tkr);
    return showCurrency ? `${sign}${formatted} tkr` : `${sign}${formatted}k`;
  }

  // Under 1 000 kr - visa hela beloppet
  return showCurrency ? `${sign}${formatNumber(abs)} kr` : `${sign}${formatNumber(abs)}`;
}

/**
 * Formatera ett tal med smart enhet baserat på storlek
 * Visar alltid tydlig enhet (kr, tkr, mkr, mdr)
 * @param value - Talet att formatera
 * @returns Objekt med value och unit separerade, samt full sträng
 */
export function formatWithUnit(value: number | null | undefined): {
  value: string;
  unit: string;
  full: string;
} {
  if (value === null || value === undefined || value === 0) {
    return { value: "0", unit: "kr", full: "0 kr" };
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";

  if (abs >= 1_000_000_000) {
    const mdr = abs / 1_000_000_000;
    const decimals = mdr >= 10 ? 0 : 1;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(mdr);
    return { value: `${sign}${formatted}`, unit: "mdr", full: `${sign}${formatted} mdr` };
  }

  if (abs >= 1_000_000) {
    const mkr = abs / 1_000_000;
    const decimals = mkr >= 10 ? 0 : 1;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(mkr);
    return { value: `${sign}${formatted}`, unit: "mkr", full: `${sign}${formatted} mkr` };
  }

  if (abs >= 1_000) {
    const tkr = abs / 1_000;
    const decimals = tkr < 10 ? 1 : 0;
    const formatted = new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(tkr);
    return { value: `${sign}${formatted}`, unit: "tkr", full: `${sign}${formatted} tkr` };
  }

  const formatted = formatNumber(abs);
  return { value: `${sign}${formatted}`, unit: "kr", full: `${sign}${formatted} kr` };
}

/**
 * Parsa en formaterad sträng tillbaka till ett tal
 * Hanterar svenska tusentalsavgränsare (mellanslag)
 * @param value - Strängen att parsa
 * @returns Talet eller 0 om parsing misslyckas
 */
export function parseFormattedNumber(value: string): number {
  if (!value || value.trim() === "") {
    return 0;
  }

  // Ta bort mellanslag, "kr", och andra icke-numeriska tecken (förutom minus och komma)
  const cleaned = value
    .replace(/\s/g, "")
    .replace(/kr/gi, "")
    .replace(/,/g, ".")
    .trim();

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : Math.round(parsed);
}

/**
 * Formatera ett datumintervall
 * @param start - Startdatum (YYYY-MM-DD)
 * @param end - Slutdatum (YYYY-MM-DD)
 */
export function formatDateRange(start: string, end: string): string {
  return `${start} - ${end}`;
}

/**
 * Formatera ett datum till svenskt format
 * @param date - Datum som sträng (YYYY-MM-DD) eller Date-objekt
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("sv-SE");
}
